import { APIFetchError } from "@cur8/api-client";
import { createElement, useCallback } from "react";
import { useAppInsights } from "render/context/AppInsightsContext";
import { useNotifications } from "render/context/NotificationContext";
import { RequestErrorNotification } from "render/ui/notification/RequestErrorNotification";

let wasHandled: unknown;

export function useReporting() {
  const { emit } = useNotifications();
  const appInsights = useAppInsights();

  const handleAPIFetchError = useCallback(
    async (error: APIFetchError, context?: string) => {
      const body = await error.response.text();

      const content = createElement(RequestErrorNotification, {
        error,
        comment: context ?? "API Error",
        body,
      });

      emit(content, "error");
      appInsights.trackException({
        exception: error,
        properties: { context, status: error.response.status },
      });
    },
    [emit, appInsights]
  );

  const handleError = useCallback(
    (error: unknown, context?: string) => {
      if (wasHandled === error) {
        return;
      }

      wasHandled = error;

      const prefix = context ? `${context}: ` : "";

      if (error instanceof APIFetchError) {
        handleAPIFetchError(error, context);
      } else if (error instanceof TypeError) {
        // The Fetch spec guarantees a network-layer failure (dropped
        // connection, offline device, DNS failure) rejects with a
        // TypeError — never an HTTP response, so never an APIFetchError.
        emit(
          `${prefix}Connection lost. Check the network and try again.`,
          "error"
        );
        appInsights.trackException({
          exception: error,
          properties: { context },
        });
      } else if (error instanceof Error) {
        if (error.name === "AbortError") {
          console.debug("Fetch aborted", { error });
        } else {
          emit(`${prefix}Error: ${error.message}`, "error");
          appInsights.trackException({
            exception: error,
            properties: { context },
          });
        }
      } else {
        emit(`${prefix}${error}`, "error");
      }
    },
    [handleAPIFetchError, emit, appInsights]
  );

  const logError = useCallback(
    (error: unknown, context?: string) => {
      handleError(error, context);
      throw error;
    },
    [handleError]
  );

  return { logError, handleError };
}
